/**
 * CR309: Hook for fetching merchant scan health metrics from v_merchants_scan_health view
 * Used in Merchant Directory to show source counts and warnings
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MerchantScanHealth {
  merchant_key: string;
  display_name: string;
  directory_status: string;
  scan_enabled: boolean;
  active_web_sources_count: number;
  active_flyer_sources_count: number;
  total_sources_count: number;
  has_web_sources_warning: boolean;
  has_flyer_sources_warning: boolean;
}

const SCAN_HEALTH_KEY = ["merchants-scan-health"];

/**
 * Fetch all merchants' scan health (for list views)
 */
export function useMerchantsScanHealth() {
  return useQuery({
    queryKey: SCAN_HEALTH_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_merchants_scan_health")
        .select("*");
      
      if (error) throw error;
      
      // Build a map for O(1) lookups
      const healthMap = new Map<string, MerchantScanHealth>();
      for (const row of data ?? []) {
        healthMap.set(row.merchant_key, row as MerchantScanHealth);
      }
      return healthMap;
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Get scan health for a single merchant (from cache or fresh)
 */
export function useMerchantScanHealth(merchantKey: string | undefined) {
  return useQuery({
    queryKey: [...SCAN_HEALTH_KEY, merchantKey],
    queryFn: async () => {
      if (!merchantKey) return null;
      
      const { data, error } = await supabase
        .from("v_merchants_scan_health")
        .select("*")
        .eq("merchant_key", merchantKey)
        .single();
      
      if (error) throw error;
      return data as MerchantScanHealth;
    },
    enabled: !!merchantKey,
  });
}
